/**
 * Dest-first on-demand charter.
 *
 * The RFQ is mintOnDemandDestination (Inbox on-demand dest, with optional
 * origin / STD / pax / tail / tech stop). The ticket is draftFromRequest —
 * the same draft Create charter files. acceptDraft on the store is the only
 * board writer. This module does not insert missions.
 */
import { hydrateSceneryAirport } from "./airports";
import { contentXmlScheduleBlocked } from "./contentXmlSchedule";
import { draftFromRequest, mintOnDemandDestination, type MintOnDemandInput } from "./inbox";
import type { RadioCompany } from "./radio";
import { requestNeedsTechStopPick } from "./techStop";
import type { CharterRequest, CompanyMxBase, DraftMission } from "./types";

export type OnDemandCharterFields = {
  destIcao: string;
  originIcao?: string;
  departureIso?: string;
  paxCount?: number;
  cargoLb?: number;
  aircraftId?: string;
  clientName?: string;
  notes?: string;
  techStopIcao?: string;
};

export type OnDemandCharterPlan =
  | { ok: true; request: CharterRequest; draft: DraftMission }
  | { ok: false; error: string };

/** Spoken / typed fields only. Does not mint a ticket. */
export function screenOnDemandCharter(
  input: OnDemandCharterFields & {
    scenery?: MintOnDemandInput["scenery"];
    scheduleOnlyContentXmlAirports?: boolean;
    preferIcaos?: string[];
  },
):
  | { ok: true; destIcao: string; originIcao?: string; departureIso?: string; techStopIcao?: string }
  | { ok: false; error: string } {
  const destIcao = (input.destIcao ?? "").trim().toUpperCase();
  if (destIcao.length < 3 || destIcao.length > 4) {
    return { ok: false, error: "Need a destination ICAO already stated. Will not invent one." };
  }
  const originIcao = (input.originIcao ?? "").trim().toUpperCase();
  if (originIcao && (originIcao.length < 3 || originIcao.length > 4)) {
    return { ok: false, error: "Origin ICAO must be 3–4 letters. Will not invent one." };
  }
  if (originIcao && originIcao === destIcao) {
    return { ok: false, error: "Destination matches the origin. Pick another ICAO." };
  }
  const techStopIcao = (input.techStopIcao ?? "").trim().toUpperCase();
  if (techStopIcao && (techStopIcao.length < 3 || techStopIcao.length > 4)) {
    return { ok: false, error: "Tech-stop ICAO must be 3–4 letters." };
  }
  let departureIso: string | undefined;
  if ((input.departureIso ?? "").trim()) {
    const parsed = new Date(input.departureIso!);
    if (Number.isNaN(parsed.getTime())) {
      return { ok: false, error: "STD must be a real Zulu time already stated. Will not invent one." };
    }
    departureIso = parsed.toISOString();
  }
  const scenery = input.scenery ?? [];
  const dest = hydrateSceneryAirport(destIcao, scenery) ?? hydrateSceneryAirport(destIcao, []);
  if (!dest) {
    return { ok: false, error: `Unknown destination ${destIcao}. Add it to Destinations or use a catalog ICAO.` };
  }
  if (originIcao) {
    const origin = hydrateSceneryAirport(originIcao, scenery) ?? hydrateSceneryAirport(originIcao, []);
    if (!origin) {
      return { ok: false, error: `Unknown origin ${originIcao}. Add it to Destinations or use a catalog ICAO.` };
    }
  }
  if (techStopIcao) {
    const stop = hydrateSceneryAirport(techStopIcao, scenery) ?? hydrateSceneryAirport(techStopIcao, []);
    if (!stop) return { ok: false, error: `Unknown tech-stop ICAO ${techStopIcao}.` };
  }
  const blocked = contentXmlScheduleBlocked(
    Boolean(input.scheduleOnlyContentXmlAirports),
    input.preferIcaos,
    [originIcao, destIcao, techStopIcao],
  );
  if (blocked) return { ok: false, error: blocked };
  return {
    ok: true,
    destIcao,
    originIcao: originIcao || undefined,
    departureIso,
    techStopIcao: techStopIcao || undefined,
  };
}

/**
 * Plan the RFQ and the draft mission. Pure — the caller files with acceptDraft.
 * A city-pair past practical range uses a Content.xml addon tech stop when one
 * splits the route. Otherwise the caller must pass a tech-stop ICAO. A runway
 * under 5000 ft stays a warning on the draft.
 */
export function planOnDemandCharter(
  input: OnDemandCharterFields & MintOnDemandInput & { company?: RadioCompany | null; mxBases?: readonly CompanyMxBase[] },
): OnDemandCharterPlan {
  const screened = screenOnDemandCharter(input);
  if (!screened.ok) return screened;
  const minted = mintOnDemandDestination({
    ...input,
    destIcao: screened.destIcao,
    originIcao: screened.originIcao,
    departureIso: screened.departureIso,
    techStopIcao: screened.techStopIcao,
  });
  if (!minted.ok) return minted;
  if (requestNeedsTechStopPick(minted.request)) {
    return {
      ok: false,
      error: "This city-pair needs a tech stop. No Content.xml addon airport splits it — type a tech-stop ICAO.",
    };
  }
  // Inbox on-demand may sketch an empty return. Schedule mint does not invent
  // that ferry — acceptDraft adds one only when the tail is not at the origin.
  // Tech-stop hops stay on proposedLegs; kind one_way keeps the return writer off.
  const request: CharterRequest = {
    ...minted.request,
    source: "manual",
    kind: "charter",
    returnEmpty: false,
    itineraryKind: "one_way",
    proposedLegs: (minted.request.proposedLegs ?? []).filter((leg) => !leg.empty),
  };
  const drafted = draftFromRequest(request, {
    scenery: input.scenery,
    fleet: input.fleet,
    aircraftId: request.suggestedAircraftId,
    now: input.now,
    ratedIcaoTypes: input.ratedIcaoTypes,
    company: input.company,
    scheduleOnlyContentXmlAirports: input.scheduleOnlyContentXmlAirports,
    contentXmlIcaos: input.preferIcaos,
    mxBases: input.mxBases,
  });
  if (!drafted.ok) return drafted;
  const warn = request.warning?.trim();
  const draft: DraftMission = warn
    ? {
        ...drafted.plan.mission,
        dispatchWarning: [drafted.plan.mission.dispatchWarning, warn].filter(Boolean).join(" · "),
      }
    : drafted.plan.mission;
  return { ok: true, request, draft };
}
